import { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { RankingTable } from '../../components/drivers/RankingTable';
import { Input } from '../../components/ui/Input';
import { Spinner } from '../../components/ui/Spinner';
import { useDrivers } from '../../hooks/useDrivers';
import { useVehicles } from '../../hooks/useVehicles';
import type { Driver } from '../../types/driver';
import { LoadError } from '../../components/common/LoadError';
import { DriverProfileModal } from '../../components/drivers/DriverProfileModal';

export default function DriversPage() {
  const [search, setSearch] = useState('');
  const { drivers: rawDrivers, isLoading, error } = useDrivers();
  const { vehicles } = useVehicles();
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);

  useEffect(() => { document.title = 'Conductores | Paitron Radar'; }, []);

  // FE-04: `Driver` real no trae el vehículo asignado — se cruza con
  // `Vehicle.driverId` (el mismo dato que ya usa el mapa).
  const drivers = useMemo(() => rawDrivers.map(d => {
    const vehicle = vehicles.find(v => v.driverId === d.id);
    return vehicle ? { ...d, assignedVehicleId: vehicle.id, assignedVehiclePlate: vehicle.plate } : d;
  }), [rawDrivers, vehicles]);

  const filtered = drivers.filter(d =>
    d.name.toLowerCase().includes(search.toLowerCase()) ||
    (d.assignedVehiclePlate ?? '').toLowerCase().includes(search.toLowerCase())
  );

  const highRisk = filtered.filter(d => d.score?.level === 'high');
  const medRisk  = filtered.filter(d => d.score?.level === 'medium');
  const lowRisk  = filtered.filter(d => d.score?.level === 'low');
  const pendingScore = filtered.filter(d => !d.score);

  return (
    <div className="drivers-page">
      {/* Stats row */}
      <div className="drivers-page__stats">
        <div className="drivers-page__stat drivers-page__stat--high">
          <span className="drivers-page__stat-value">{highRisk.length}</span>
          <span className="drivers-page__stat-label">Alto riesgo 🔴</span>
        </div>
        <div className="drivers-page__stat drivers-page__stat--medium">
          <span className="drivers-page__stat-value">{medRisk.length}</span>
          <span className="drivers-page__stat-label">Riesgo medio 🟡</span>
        </div>
        <div className="drivers-page__stat drivers-page__stat--low">
          <span className="drivers-page__stat-value">{lowRisk.length}</span>
          <span className="drivers-page__stat-label">Riesgo bajo 🟢</span>
        </div>
        <div className="drivers-page__stat">
          <span className="drivers-page__stat-value">{pendingScore.length}</span>
          <span className="drivers-page__stat-label">Score pendiente ⏳</span>
        </div>
        <div className="drivers-page__stat">
          <span className="drivers-page__stat-value">{filtered.length}</span>
          <span className="drivers-page__stat-label">Total conductores</span>
        </div>
      </div>

      {/* Search */}
      <div className="drivers-page__search">
        <Input
          id="drivers-search"
          placeholder="Buscar por nombre o placa..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          leftIcon={<Search size={15} />}
        />
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="drivers-page__loading"><Spinner size="lg" /></div>
      ) : error && rawDrivers.length === 0 ? (
        <LoadError what="los conductores" error={error} />
      ) : (
        <RankingTable
          drivers={filtered}
          onDriverClick={d => setSelectedDriver(d)}
        />
      )}

      {/* Driver Profile Modal */}
      <DriverProfileModal 
        isOpen={selectedDriver !== null} 
        onClose={() => setSelectedDriver(null)} 
        driver={selectedDriver} 
      />
    </div>
  );
}
